import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { ChevronLeft, ChevronRight, Check, X, Minus } from 'lucide-react';

export default function MeetingsPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedCell, setSelectedCell] = useState(null); // { employeeId, date, name }
  const [notes, setNotes] = useState('');
  const queryClient = useQueryClient();

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date().toISOString().split('T')[0];

  const monthStart = `${year}-${String(month + 1).padStart(2, '0')}-01`;
  const monthEnd = `${year}-${String(month + 1).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;

  // Fetch employees (team members)
  const { data: usersData } = useQuery({
    queryKey: ['team-members'],
    queryFn: async () => {
      const res = await api.get('/api/users', { params: { role: 'EMPLOYEE' } });
      return res.data.data || res.data || [];
    },
  });
  const employees = Array.isArray(usersData) ? usersData : [];

  // Fetch meetings for the month
  const { data: meetingsData, isLoading } = useQuery({
    queryKey: ['meetings', year, month],
    queryFn: async () => {
      const res = await api.get('/api/meetings', { params: { from: monthStart, to: monthEnd } });
      return res.data.data || [];
    },
  });

  // Build nested lookup: meetingMap[employeeId][date] = meeting
  const meetingMap = useMemo(() => {
    const map = {};
    if (meetingsData) {
      meetingsData.forEach((m) => {
        if (!map[m.employeeId]) map[m.employeeId] = {};
        map[m.employeeId][m.date] = m;
      });
    }
    return map;
  }, [meetingsData]);

  const saveMutation = useMutation({
    mutationFn: async (payload) => {
      const res = await api.post('/api/meetings', payload);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meetings', year, month] });
      setSelectedCell(null);
      setNotes('');
    },
  });

  const handleCellClick = (employee, dayNum) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
    if (dateStr > today) return; // no future dates
    const existing = meetingMap[employee.id]?.[dateStr];
    setNotes(existing?.notes || '');
    setSelectedCell({ employeeId: employee.id, date: dateStr, name: employee.name });
  };

  const handleSave = (conducted) => {
    saveMutation.mutate({
      date: selectedCell.date,
      employeeId: selectedCell.employeeId,
      conducted,
      notes: notes || null,
    });
  };

  const monthName = new Date(year, month).toLocaleString('en-US', { month: 'long', year: 'numeric' });

  // Only show days 1–today for current month, all days for past months
  const visibleDays = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const fmt = (dateStr) => new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Manager Check-ins</h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Track daily check-ins with each team member
        </p>
      </div>

      {/* Month navigation */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-4">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setCurrentDate(new Date(year, month - 1))}
            className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{monthName}</h2>
          <button
            onClick={() => setCurrentDate(new Date(year, month + 1))}
            className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition"
          >
            <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Legend */}
        <div className="flex gap-6 text-sm text-gray-600 dark:text-gray-400">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-green-50 dark:bg-slate-700 border border-green-300 dark:border-green-800 rounded flex items-center justify-center">
              <Check className="w-3 h-3 text-green-600 dark:text-green-400" />
            </div>
            <span>Checked in</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-red-50 dark:bg-slate-700 border border-red-300 dark:border-red-800 rounded flex items-center justify-center">
              <X className="w-3 h-3 text-red-600 dark:text-red-400" />
            </div>
            <span>Missed</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-gray-100 dark:bg-slate-700 rounded border border-gray-300 dark:border-slate-600 flex items-center justify-center">
              <Minus className="w-3 h-3 text-gray-400 dark:text-gray-500" />
            </div>
            <span>No record</span>
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-x-auto">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading check-ins...</div>
        ) : employees.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">No employees found.</div>
        ) : (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-slate-700">
                {/* Sticky employee name column */}
                <th className="sticky left-0 bg-white dark:bg-slate-800 px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300 min-w-[140px] z-10 border-r border-gray-200 dark:border-slate-700">
                  Employee
                </th>
                {visibleDays.map((day) => {
                  const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  const isToday = dateStr === today;
                  const dow = new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short' });
                  return (
                    <th
                      key={day}
                      className={`px-1 py-2 text-center font-medium min-w-[36px] ${isToday ? 'text-blue-600 dark:text-blue-400 font-bold' : 'text-gray-500 dark:text-gray-400'}`}
                    >
                      <div className="text-xs">{dow}</div>
                      <div>{day}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {employees.map((emp) => (
                <tr key={emp.id} className="border-b border-gray-100 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700/30">
                  <td className="sticky left-0 bg-white dark:bg-slate-800 px-4 py-2 font-medium text-gray-900 dark:text-white border-r border-gray-200 dark:border-slate-700 z-10">
                    {emp.name}
                  </td>
                  {visibleDays.map((day) => {
                    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                    const isFuture = dateStr > today;
                    const isToday = dateStr === today;
                    const meeting = meetingMap[emp.id]?.[dateStr];

                    let cellContent = <Minus className="w-3 h-3 text-gray-300 dark:text-slate-600" />;
                    let cellBg = '';

                    if (meeting?.conducted) {
                      cellContent = <Check className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />;
                      cellBg = 'bg-green-50 dark:bg-green-900/20';
                    } else if (meeting && !meeting.conducted) {
                      cellContent = <X className="w-3.5 h-3.5 text-red-500 dark:text-red-400" />;
                      cellBg = 'bg-red-50 dark:bg-red-900/20';
                    }

                    return (
                      <td key={day} className={`px-1 py-2 text-center ${cellBg} ${isToday ? 'ring-1 ring-inset ring-blue-400' : ''}`}>
                        <button
                          onClick={() => handleCellClick(emp, day)}
                          disabled={isFuture}
                          className={`w-7 h-7 rounded flex items-center justify-center mx-auto transition ${isFuture ? 'cursor-not-allowed opacity-30' : 'hover:bg-gray-200 dark:hover:bg-slate-600 cursor-pointer'}`}
                          title={isFuture ? '' : `${emp.name} — ${fmt(dateStr)}`}
                        >
                          {cellContent}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {selectedCell && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl p-6 max-w-md w-full mx-4 border border-gray-200 dark:border-slate-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
              Check-in — {selectedCell.name}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{fmt(selectedCell.date)}</p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Notes (optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="How did the check-in go? Any action items?"
                maxLength={1000}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white dark:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-600 resize-none text-sm"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{notes.length}/1000</p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => handleSave(true)}
                disabled={saveMutation.isPending}
                className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-lg transition flex items-center justify-center gap-2 text-sm"
              >
                <Check className="w-4 h-4" /> Checked In
              </button>
              <button
                onClick={() => handleSave(false)}
                disabled={saveMutation.isPending}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-lg transition flex items-center justify-center gap-2 text-sm"
              >
                <X className="w-4 h-4" /> Missed
              </button>
              <button
                onClick={() => { setSelectedCell(null); setNotes(''); }}
                className="bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition text-sm border border-gray-200 dark:border-slate-600"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
